import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AlertCircle, Loader2, Pause, Play, Radio, Volume2 } from "lucide-react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const describeWeather = (weather, location, tempSymbol, convertTemp, severeAlerts = []) => {
  const place = location?.name || weather?.name || "your current dashboard location";
  const currentTemp = weather?.main?.temp !== undefined ? `${convertTemp(weather.main.temp)} degrees ${tempSymbol}` : "current temperature unavailable";
  const condition = weather?.weather?.[0]?.description || "current conditions unavailable";
  const high = weather?.main?.temp_max !== undefined ? `${convertTemp(weather.main.temp_max)} degrees` : null;
  const low = weather?.main?.temp_min !== undefined ? `${convertTemp(weather.main.temp_min)} degrees` : null;
  const wind = weather?.wind?.speed !== undefined ? `Winds are near ${Math.round(weather.wind.speed)} miles per hour.` : "";
  const humidity = weather?.main?.humidity !== undefined ? `Humidity is ${weather.main.humidity} percent.` : "";
  const alertText = severeAlerts?.length ? `${severeAlerts.length} weather alert${severeAlerts.length === 1 ? "" : "s"} are active. Check the alert panel before making weather-sensitive decisions.` : "No active severe weather alerts are shown on the dashboard.";
  const highLow = high && low ? `Today's high is near ${high}, with a low near ${low}.` : "";

  return `Local weather briefing for ${place}. Conditions are ${condition}, with ${currentTemp}. ${highLow} ${wind} ${humidity} ${alertText}`;
};

export function WeatherRadioPlayer({ location, weather, severeAlerts, isDarkMode, tempSymbol, convertTemp }) {
  const audioRef = useRef(null);
  const [radioInfo, setRadioInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (location?.lat === undefined || location?.lat === null || location?.lon === undefined || location?.lon === null) return;

    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/radio/nearest`, { params: { lat: location.lat, lon: location.lon }, timeout: 12000 })
      .then((response) => {
        if (!cancelled) setRadioInfo(response.data);
      })
      .catch(() => {
        if (!cancelled) setRadioInfo(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [location?.lat, location?.lon]);

  useEffect(() => () => {
    audioRef.current?.pause();
    window.speechSynthesis?.cancel();
  }, []);

  const briefing = useMemo(
    () => describeWeather(weather, location, tempSymbol, convertTemp, severeAlerts),
    [weather, location, tempSymbol, convertTemp, severeAlerts]
  );

  const station = radioInfo?.station;
  const canStream = Boolean(radioInfo?.mode === "stream" && station?.stream_url);
  const statusLabel = station ? `${station.callsign} ${station.frequency}` : "Local forecast audio";
  const sourceLabel = station
    ? `${canStream ? "Live stream" : "Forecast briefing"} • ${station.city}, ${station.state}`
    : "Dashboard location";

  const stopAudio = useCallback(() => {
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.removeAttribute("src");
    window.speechSynthesis?.cancel();
    setMode("idle");
  }, []);

  const speakBriefing = useCallback((prefix = "") => {
    if (!("speechSynthesis" in window)) {
      setMessage("Audio briefing is not supported in this browser.");
      setMode("idle");
      return;
    }

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(`${prefix} ${briefing}`.trim());
    utterance.rate = 0.94;
    utterance.pitch = 0.95;
    utterance.onend = () => setMode("idle");
    utterance.onerror = () => {
      setMessage("Audio briefing stopped before finishing.");
      setMode("idle");
    };
    window.speechSynthesis.speak(utterance);
    setMode("speech");
  }, [briefing]);

  const playAudio = useCallback(async () => {
    setMessage("");

    if (canStream && audioRef.current) {
      audioRef.current.src = station.stream_url;
      try {
        await audioRef.current.play();
        setMode("stream");
        return;
      } catch {
        setMessage("Live stream unavailable. Playing local briefing.");
      }
    }

    speakBriefing(canStream ? "The live weather radio stream is unavailable. Here is your local forecast briefing." : "Here is your local forecast briefing.");
  }, [canStream, station?.stream_url, speakBriefing]);

  const containerClass = isDarkMode
    ? "border-white/10 bg-black/35 text-white"
    : "border-slate-300 bg-white/85 text-slate-950";
  const subTextClass = isDarkMode ? "text-white/55" : "text-slate-600";

  return (
    <div
      className={`col-span-1 flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-full border p-0 shadow-sm backdrop-blur-xl sm:w-[218px] sm:min-w-[218px] sm:justify-start sm:rounded-xl sm:px-2 ${containerClass}`}
      data-testid="weather-radio-player"
      title={`${statusLabel} — ${sourceLabel}`}
    >
      <div className={`hidden h-6 w-6 shrink-0 items-center justify-center rounded-full sm:flex ${isDarkMode ? "bg-[#00f2ea]/10 text-[#00f2ea]" : "bg-cyan-100 text-cyan-700"}`} data-testid="weather-radio-icon">
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radio className="h-3.5 w-3.5" />}
      </div>
      <div className="hidden min-w-0 flex-1 sm:block" data-testid="weather-radio-info">
        <div className="flex min-w-0 items-center gap-1 leading-none">
          <span className="truncate text-[11px] font-black leading-none" data-testid="weather-radio-status-label">{statusLabel}</span>
          {mode !== "idle" && <Volume2 className="h-3 w-3 shrink-0 text-[#00f2ea]" data-testid="weather-radio-playing-icon" />}
        </div>
        <p className={`mt-0.5 truncate text-[9px] leading-none ${subTextClass}`} data-testid="weather-radio-source-label">
          {message || sourceLabel}
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={mode === "idle" ? playAudio : stopAudio}
        className={`h-full w-full shrink-0 rounded-full p-0 sm:h-7 sm:w-7 sm:rounded-lg ${isDarkMode ? "hover:bg-white/10" : "hover:bg-slate-100"}`}
        data-testid="weather-radio-play-toggle-button"
        aria-label={mode === "idle" ? "Play weather radio" : "Stop weather radio"}
      >
        {mode === "idle" ? <Play className="h-4 w-4 sm:h-3.5 sm:w-3.5" /> : <Pause className="h-4 w-4 sm:h-3.5 sm:w-3.5" />}
      </Button>
      {radioInfo?.note && (
        <span className="sr-only" data-testid="weather-radio-safety-note">{radioInfo.note}</span>
      )}
      {message && <AlertCircle className="hidden h-4 w-4 shrink-0 text-amber-400 sm:block" data-testid="weather-radio-message-icon" />}
      <audio
        ref={audioRef}
        data-testid="weather-radio-audio-element"
        onError={() => {
          if (mode === "stream") {
            setMessage("Live stream unavailable. Playing local briefing.");
            speakBriefing("The live weather radio stream is unavailable. Here is your local forecast briefing.");
          }
        }}
        onEnded={() => setMode("idle")}
      />
    </div>
  );
}